import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BsCircleFill } from 'react-icons/bs';
import moment, { Moment } from 'moment';
import { momentDateFormat } from '@utils/formatters';
import { IDevice } from '@pages/Assets/Inventory/types';
import { IDeviceActionRequestPayload } from '../../types';
import useNetworkBlockUploadStep from './NetworkBlockUploadStep';

import './styles.less';
import useNetWorkBlock from './NetworkBlock';
import useNetworkUnblock from './NetworkUnblock';
import useIntroStep from '../IntroStep';

interface IUseNetworBlockProps {
  handleDeviceActions: (item: IDeviceActionRequestPayload) => void;
  actionKey: number;
  devicesProps: IDevice[] | undefined;
  currentStep: number;
  uploadedDevicesState: any;
}

function useNetworAction({
  devicesProps,
  handleDeviceActions,
  currentStep,
  uploadedDevicesState,
}: IUseNetworBlockProps) {
  const { t: translate } = useTranslation(['device-actions-modal']);
  const [extraAction, setextraAction] = useState(20);
  const [actionDisabled, setActionDisabled] = useState(0);
  const [blockingType, setBlockingType] = useState(1);
  const [blockStartDate, setBlockStartDate] = useState<Moment | null>(moment());
  const [blockEndDate, setBlockEndDate] = useState<Moment | null>(null);
  const [unblockDate, setUnblockDate] = useState<Moment | null>(moment());
  const [uploadedDevices, setUploadedDevices] = uploadedDevicesState;

  const dontHaveDevicesSelects = !devicesProps || devicesProps?.length === 0;
  // Separate devices if both selected
  const devicesByBlockingType = useMemo(() => {
    if (devicesProps) {
      const grouped = devicesProps?.reduce((acc: any, current: any) => {
        if (current?.networkBlockStatus !== 'NONE') {
          return {
            ...acc,
            devicesToUnblock: [...(acc.devicesToUnblock || []), current],
          };
        }
        return {
          ...acc,
          devicesToBlock: [...(acc.devicesToBlock || []), current],
        };
      }, {});

      if (devicesProps?.length > 0) {
        if (
          grouped?.devicesToUnblock?.length > 0 &&
          (!grouped?.devicesToBlock || grouped?.devicesToBlock?.length === 0)
        ) {
          setextraAction(21);
          setActionDisabled(20);
        } else if (
          grouped?.devicesToBlock?.length > 0 &&
          (!grouped?.devicesToUnblock ||
            grouped?.devicesToUnblock?.length === 0)
        ) {
          setextraAction(20);
          setActionDisabled(21);
        } else if (
          grouped?.devicesToBlock?.length > 0 &&
          grouped?.devicesToUnblock?.length > 0
        ) {
          setextraAction(20);
          setActionDisabled(0);
        }
      } else {
        setextraAction(20);
        setActionDisabled(0);
      }

      return grouped;
    }
    return {};
  }, [devicesProps]);

  const renderParamTitle = useCallback(
    (title: string) => {
      return (
        <div className="param-label">
          <BsCircleFill />
          <b>{translate(`steps.network-block.${title}`)}</b>
        </div>
      );
    },
    [translate],
  );
  const { step, uploadReinitState } = useNetworkBlockUploadStep({
    translate,
    actionKey: extraAction,
    setUploadedDevices,
  });

  const { netWorkBlockStep } = useNetWorkBlock({
    renderParamTitle,
    setBlockingType,
    blockingType,
    blockStartDate,
    setBlockStartDate,
    blockEndDate,
    setBlockEndDate,
  });

  const { netWorkUnblockStep } = useNetworkUnblock({
    renderParamTitle,
    unblockDate,
    setUnblockDate,
  });

  const { introStep } = useIntroStep({
    devicesByBlockingType,
    extraAction,
    setextraAction,
    actionDisabled,
    devicesProps,
    uploadReinitState,
  });

  const configurationStep = {
    title: translate('steps.network-block.step2'),
    content: (
      <>
        {extraAction === 20 && netWorkBlockStep}
        {extraAction === 21 && netWorkUnblockStep}
      </>
    ),
    onPrevStep: () => {
      setBlockingType(1);
      setBlockStartDate(moment());
      setBlockEndDate(null);
      setUnblockDate(null);
    },
  };

  const reinitState = () => {
    uploadReinitState();
    setBlockingType(1);
    setBlockStartDate(moment());
    setBlockEndDate(null);
    setUnblockDate(null);
  };

  const contentSteps = [
    ...(!dontHaveDevicesSelects
      ? [introStep, configurationStep]
      : [introStep, step]),
  ];

  const onSubmit = useCallback(async () => {
    const payload: IDeviceActionRequestPayload = {};
    payload.action = extraAction;

    if (dontHaveDevicesSelects) {
      if (extraAction === 20) {
        payload.devices = uploadedDevices.map((uploaded: any) => {
          const { msisdn, ...rest } = uploaded;
          let processData = { ...rest };
          if (!processData.dtInicioBloqueio) {
            processData = {
              ...processData,
              dtInicioBloqueio: momentDateFormat(moment(), 'YYYYMMDD'),
            };
          }
          if (processData.dtFimBloqueio === '') {
            processData = {
              ...processData,
              dtFimBloqueio: undefined,
            };
          }
          processData = JSON.stringify(processData);
          return {
            msisdn,
            processData,
          };
        });
      } else {
        payload.devices = uploadedDevices.map(
          ({ msisdn, dtDesbloqueio }: any) => {
            return {
              msisdn,
              processData: JSON.stringify({
                dtDesbloqueio:
                  dtDesbloqueio !== ''
                    ? dtDesbloqueio
                    : momentDateFormat(moment(), 'YYYYMMDD'),
              }),
            };
          },
        );
      }
    } else if (extraAction === 20) {
      payload.devices = devicesByBlockingType.devicesToBlock.map(
        (device: any) => {
          return {
            msisdn: device.msisdn,
            processData: JSON.stringify({
              tpBloqueio: blockingType,
              dtInicioBloqueio: momentDateFormat(blockStartDate, 'YYYYMMDD'),
              dtFimBloqueio:
                blockingType === 3 && blockEndDate
                  ? momentDateFormat(blockEndDate, 'YYYYMMDD')
                  : undefined,
            }),
          };
        },
      );
    } else {
      payload.devices = devicesByBlockingType.devicesToUnblock.map(
        (device: any) => {
          return {
            msisdn: device.msisdn,
            processData: JSON.stringify({
              dtDesbloqueio: momentDateFormat(unblockDate, 'YYYYMMDD'),
            }),
          };
        },
      );
    }
    return handleDeviceActions(payload);
  }, [
    handleDeviceActions,
    blockEndDate,
    blockStartDate,
    blockingType,
    devicesByBlockingType.devicesToBlock,
    devicesByBlockingType.devicesToUnblock,
    extraAction,
    unblockDate,
    uploadedDevices,
    dontHaveDevicesSelects,
  ]);

  const nextStepIsDisabled = useCallback(() => {
    if (
      currentStep === 1 &&
      extraAction === 20 &&
      blockingType === 3 &&
      (blockEndDate === null || !blockEndDate)
    ) {
      return true;
    }

    if (
      currentStep === 1 &&
      extraAction === 21 &&
      blockingType === 1 &&
      (unblockDate === null || !unblockDate) &&
      uploadedDevices.length === 0
    ) {
      return true;
    }

    if (
      currentStep === 1 &&
      dontHaveDevicesSelects &&
      uploadedDevices.length === 0
    ) {
      return true;
    }

    return false;
  }, [
    currentStep,
    extraAction,
    blockingType,
    blockEndDate,
    unblockDate,
    uploadedDevices.length,
    dontHaveDevicesSelects,
  ]);

  const networkBlockData = {
    contentSteps,
    onSubmit,
    reinitState,
    nextStepIsDisabled,
    extraAction,
  };

  return { networkBlockData };
}

export default useNetworAction;
